import { useState, useEffect, useCallback, useRef } from "react"
import Markdown from "react-markdown"
import {
  ChevronDown, ChevronRight, Cpu, Activity, Target,
  Clock, Play, AlertTriangle, CheckCircle, FileText,
  Zap, Shield, RefreshCw, Layers, Search, Wrench, ClipboardCheck,
} from "lucide-react"
import ChecksPanel from "@/components/ChecksPanel"
import {
  getCommand, getStatusSnapshot, getSloStatus,
  executeSuggestedRunbook, generateShiftReport, getDiagnosis,
  type CommandData, type GpuWorker, type StatusSnapshot,
} from "@/lib/api"
import { timeAgo } from "@/lib/utils"

// ── Section accordion ────────────────────────────────────────────────────

function Section({ title, badge, icon: Icon, children, defaultOpen }: {
  title: string
  badge: string
  icon: typeof Cpu
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen || false)
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center w-full px-4 py-3 gap-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <Icon size={16} className="text-gray-500 shrink-0" />
        <span className="text-sm font-medium text-gray-200 flex-1">{title}</span>
        <span className="text-xs text-gray-500 font-mono">{badge}</span>
        {open ? <ChevronDown size={14} className="text-gray-600" /> : <ChevronRight size={14} className="text-gray-600" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  )
}

// ── GPU Worker card ──────────────────────────────────────────────────────

function GpuWorkerCard({ worker }: { worker: GpuWorker }) {
  const utilPct = worker.gpu_utilization ?? 0
  const vramGB = (worker.gpu_vram_mb / 1024).toFixed(0)
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
      <div className={`w-2 h-2 rounded-full shrink-0 ${worker.alive ? (worker.ready ? "bg-green-500" : "bg-amber-500") : "bg-red-500"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-200 truncate">{worker.gpu_name}</div>
        <div className="text-xs text-gray-500">{vramGB} GB VRAM</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-mono text-gray-300">{utilPct}%</div>
        <div className="text-[10px] text-gray-600">
          {worker.ready ? "ready" : worker.alive ? "loading" : "offline"}
        </div>
      </div>
    </div>
  )
}

// ── Hero cards ───────────────────────────────────────────────────────────

function HeroNeedsYou({ cmd, onRunFix }: { cmd: CommandData; onRunFix: () => void }) {
  const cc = cmd.command_center || {}
  const confidence = cc.confidence_pct ?? cc.confidence ?? null
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-white">{cmd.headline}</h2>
          <p className="text-sm text-gray-400 mt-1">{cmd.subtitle}</p>
        </div>
      </div>
      {(confidence !== null || cc.suggested_runbook) && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {confidence !== null && <span>Confidence: {confidence}%</span>}
          {cc.impact && <span>Impact: {cc.impact}</span>}
        </div>
      )}
      {cc.suggested_runbook && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">
            Recommended: <span className="text-gray-300 font-mono">{cc.suggested_runbook}</span>
          </p>
          <button
            onClick={onRunFix}
            className="w-full py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
          >
            Run Fix
          </button>
        </div>
      )}
      <SnapshotLine snapshot={cmd.snapshot} />
    </div>
  )
}

function HeroIssues({ cmd }: { cmd: CommandData }) {
  const incident = cmd.incident || {}
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 space-y-3">
      <div className="flex items-start gap-3">
        <Activity size={20} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-white">{cmd.headline}</h2>
          <p className="text-sm text-gray-400 mt-1">{cmd.subtitle}</p>
        </div>
      </div>
      {incident.ownership_state && (
        <div className="text-xs text-gray-500">
          Status: <span className="text-gray-400">{incident.ownership_state}</span>
          {incident.source_agent && <span> · Agent: {incident.source_agent}</span>}
        </div>
      )}
      <SnapshotLine snapshot={cmd.snapshot} />
    </div>
  )
}

function HeroAllClear({ cmd, onGenerateReport, generating }: { cmd: CommandData; onGenerateReport: () => void; generating?: boolean }) {
  const s = cmd.snapshot
  return (
    <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-5 space-y-3">
      <div className="flex items-start gap-3">
        <CheckCircle size={20} className="text-green-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-white">{cmd.headline}</h2>
          <p className="text-sm text-gray-400 mt-1">{cmd.subtitle}</p>
        </div>
      </div>
      <SnapshotLine snapshot={s} />
      {cmd.jobs_total !== undefined && (
        <div className="text-xs text-gray-500">
          Total jobs processed: <span className="text-gray-400 font-mono">{cmd.jobs_total.toLocaleString()}</span>
        </div>
      )}
      <button
        onClick={onGenerateReport}
        disabled={generating}
        className="w-full py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-sm transition-colors border border-border disabled:opacity-50 disabled:cursor-wait"
      >
        {generating ? (
          <span className="flex items-center justify-center gap-2">
            <RefreshCw size={14} className="animate-spin" />
            Generating...
          </span>
        ) : (
          "Generate Shift Report"
        )}
      </button>
    </div>
  )
}

function HeroShiftReport({ cmd, onDismiss }: { cmd: CommandData; onDismiss: () => void }) {
  const sr = cmd.shift_report || {}
  return (
    <div className="rounded-xl border border-accent/30 bg-accent/5 p-5 space-y-3">
      <div className="flex items-start gap-3">
        <FileText size={20} className="text-accent shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-white">{cmd.headline}</h2>
        </div>
        <button onClick={onDismiss} className="text-gray-500 hover:text-gray-300 text-xs px-2 py-1 rounded hover:bg-white/5">Dismiss</button>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <StatBox label="Jobs" value={sr.jobs_total ?? 0} />
        <StatBox label="Alerts" value={sr.alerts_total ?? 0} />
        <StatBox label="Actions" value={sr.autopilot_actions ?? 0} />
      </div>
      <div className="prose prose-invert prose-sm max-w-none [&_p]:text-sm [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:my-1 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h3]:text-gray-200 [&_h3]:mt-2 [&_h3]:mb-1 [&_strong]:text-gray-200 [&_ul]:text-sm [&_ul]:text-gray-300 [&_ul]:my-1 [&_li]:my-0.5 [&_hr]:my-2 [&_hr]:border-border/30">
        <Markdown>{cmd.subtitle}</Markdown>
      </div>
      <SnapshotLine snapshot={cmd.snapshot} />
    </div>
  )
}

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-card border border-border px-2 py-2">
      <div className="text-lg font-mono text-white">{value}</div>
      <div className="text-[10px] text-gray-500">{label}</div>
    </div>
  )
}

function SnapshotLine({ snapshot }: { snapshot: CommandData["snapshot"] }) {
  const s = snapshot
  if (!s) return null
  const parts: string[] = []
  if (s.gpu_workers_total > 0) {
    parts.push(`GPU: ${s.gpu_workers_ready}/${s.gpu_workers_total} ready`)
  }
  if (s.throughput_jobs_per_min > 0) {
    parts.push(`${s.throughput_jobs_per_min} jobs/min`)
  }
  if (s.throughput_avg_s > 0) {
    parts.push(`avg ${s.throughput_avg_s}s`)
  }
  if (s.queue_depth > 0) {
    parts.push(`${s.queue_depth} queued`)
  }
  if (parts.length === 0) return null
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <Zap size={10} className="shrink-0" />
      {parts.join(" · ")}
    </div>
  )
}

// ── SLO row ──────────────────────────────────────────────────────────────

function SloRow({ slo }: { slo: any }) {
  const met = slo.met ?? slo.status === "met"
  return (
    <div className="flex items-center gap-3 py-2">
      <div className={`w-2 h-2 rounded-full shrink-0 ${met ? "bg-green-500" : "bg-red-500"}`} />
      <span className="text-sm text-gray-300 flex-1">{slo.label || slo.name}</span>
      <span className="text-xs font-mono text-gray-500">
        {slo.current !== undefined ? `${slo.current}${slo.unit || ""}` : met ? "OK" : "MISS"}
      </span>
    </div>
  )
}

// ── Autopilot entry ──────────────────────────────────────────────────────

function AutopilotEntry({ entry }: { entry: CommandData["autopilot_recent"][0] }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Play size={12} className="text-accent shrink-0 mt-1" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-300 truncate">{entry.action}</div>
        {entry.detail && <div className="text-xs text-gray-500 truncate">{entry.detail}</div>}
      </div>
      <span className="text-[10px] text-gray-600 shrink-0">{timeAgo(entry.time)}</span>
    </div>
  )
}

// ── Main screen ──────────────────────────────────────────────────────────

export default function CommandScreen() {
  const [cmd, setCmd] = useState<CommandData | null>(null)
  const [snapshot, setSnapshot] = useState<StatusSnapshot | null>(null)
  const [slos, setSlos] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [diagnosis, setDiagnosis] = useState<any>(null)
  const [diagnosing, setDiagnosing] = useState(false)
  const [shiftReportOverride, setShiftReportOverride] = useState<CommandData | null>(null)
  const mountRef = useRef(true)

  const loadCommand = useCallback(async () => {
    try {
      const data = await getCommand()
      if (mountRef.current) setCmd(data)
    } catch (e) {
      console.warn("Command fetch error:", e)
    }
  }, [])

  const loadSnapshot = useCallback(async () => {
    try {
      const data = await getStatusSnapshot()
      if (mountRef.current) setSnapshot(data)
    } catch {}
  }, [])

  const loadSlos = useCallback(async () => {
    try {
      const data = await getSloStatus()
      if (!mountRef.current) return
      // Backend returns {slos: {name: {…}, …}} object — convert to array
      const raw = data.slos || data || {}
      if (Array.isArray(raw)) {
        setSlos(raw)
      } else if (typeof raw === "object") {
        setSlos(Object.entries(raw).map(([key, s]: [string, any]) => ({
          name: key,
          label: s.slo_name || key.replace(/_/g, " "),
          ...s,
        })))
      } else {
        setSlos([])
      }
    } catch {}
  }, [])

  useEffect(() => {
    mountRef.current = true
    // Initial load
    Promise.all([loadCommand(), loadSnapshot(), loadSlos()]).then(() => {
      if (mountRef.current) setLoading(false)
    })

    // Polling intervals
    const cmdInterval = setInterval(loadCommand, 15_000)
    const snapInterval = setInterval(loadSnapshot, 5_000)
    const sloInterval = setInterval(loadSlos, 30_000)

    return () => {
      mountRef.current = false
      clearInterval(cmdInterval)
      clearInterval(snapInterval)
      clearInterval(sloInterval)
    }
  }, [loadCommand, loadSnapshot, loadSlos])

  const handleRunFix = useCallback(async () => {
    if (!cmd?.incident?.id) return
    try {
      await executeSuggestedRunbook(cmd.incident.id)
      loadCommand()
    } catch (e) {
      console.error("Run fix error:", e)
    }
  }, [cmd, loadCommand])

  const handleGenerateReport = useCallback(async () => {
    setGenerating(true)
    try {
      const report = await generateShiftReport()
      // Store in override state so polling doesn't overwrite it
      if (report && report.summary) {
        setShiftReportOverride({
          mode: "shift_report" as const,
          headline: "Shift Report",
          subtitle: report.summary,
          shift_report: {
            summary: report.summary,
            overall_status: report.overall_status || "all_clear",
            duration_hours: report.duration_hours || 0,
            alerts_total: report.alerts?.total || 0,
            alerts_unresolved: report.alerts?.unresolved?.length || 0,
            autopilot_actions: report.autopilot_actions?.length || 0,
            jobs_total: report.jobs?.total || 0,
          },
        } as CommandData)
      }
    } catch (e) {
      console.error("Generate report error:", e)
    } finally {
      setGenerating(false)
    }
  }, [])

  const handleDiagnose = useCallback(async () => {
    setDiagnosing(true)
    try {
      const result = await getDiagnosis()
      if (mountRef.current) setDiagnosis(result)
    } catch (e) {
      console.error("Diagnose error:", e)
    } finally {
      setDiagnosing(false)
    }
  }, [])

  // Derive header status
  const mode = cmd?.mode || "all_clear"
  const statusColor = mode === "needs_you" ? "bg-red-500" : mode === "issues" ? "bg-amber-500" : "bg-green-500"
  const statusText = mode === "needs_you" ? "Needs Intervention" : mode === "issues" ? `${cmd?.headline || "Issue"}` : "All Clear"

  // GPU workers from live snapshot (more real-time) or fallback to command data
  const gpuWorkers: GpuWorker[] = snapshot?.gpu_workers || cmd?.snapshot?.gpu_workers || []
  const gpuTotal = snapshot?.gpu_workers_total ?? cmd?.snapshot?.gpu_workers_total ?? 0
  const gpuAlive = snapshot?.gpu_workers_alive ?? cmd?.snapshot?.gpu_workers_alive ?? 0
  const gpuReady = snapshot?.gpu_workers_ready ?? cmd?.snapshot?.gpu_workers_ready ?? 0

  // Autopilot data
  const autopilotRecent = cmd?.autopilot_recent || []

  // Queue data from snapshot
  const queueDepth = snapshot?.queue_depth ?? cmd?.snapshot?.queue_depth ?? 0
  const queuePub = snapshot?.queue_pub ?? 0
  const queuePri = snapshot?.queue_pri ?? 0
  const queueDefault = snapshot?.queue_default ?? 0
  const activeJobs = snapshot?.active_jobs ?? 0
  const throughputRate = snapshot?.throughput_jobs_per_min ?? cmd?.snapshot?.throughput_jobs_per_min ?? 0
  const isStuck = queueDepth > 0 && throughputRate === 0 && activeJobs === 0
  const isBackedUp = queueDepth > 20

  // SLO summary
  const slosMet = Array.isArray(slos) ? slos.filter((s: any) => s.met ?? s.status === "met").length : 0
  const slosTotal = Array.isArray(slos) ? slos.length : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw size={20} className="text-gray-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-surface sticky top-0 z-10">
        <div className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
        <span className="text-sm font-medium text-gray-200">{statusText}</span>
        <div className="flex-1" />
        {snapshot && (
          <span className="text-[10px] text-gray-600 font-mono">
            {snapshot.total_cloaks?.toLocaleString()} total jobs
          </span>
        )}
      </div>

      {/* Hero Card */}
      <div className="p-4">
        {shiftReportOverride ? (
          <HeroShiftReport cmd={shiftReportOverride} onDismiss={() => setShiftReportOverride(null)} />
        ) : (
          <>
            {cmd && mode === "needs_you" && <HeroNeedsYou cmd={cmd} onRunFix={handleRunFix} />}
            {cmd && mode === "issues" && <HeroIssues cmd={cmd} />}
            {cmd && mode === "all_clear" && (
              <HeroAllClear cmd={cmd} onGenerateReport={handleGenerateReport} generating={generating} />
            )}
          </>
        )}
      </div>

      {/* Expandable Sections */}
      <div className="border-t border-border">
        {/* GPU Workers */}
        <Section
          title="GPU Workers"
          badge={gpuTotal > 0 ? `${gpuReady}/${gpuTotal} ready` : "none"}
          icon={Cpu}
          defaultOpen={gpuTotal > 0 && gpuReady < gpuTotal}
        >
          {gpuWorkers.length > 0 ? (
            <div className="space-y-2">
              {gpuWorkers.map(w => <GpuWorkerCard key={w.id} worker={w} />)}
              {(snapshot?.throughput_jobs_per_min ?? cmd?.snapshot?.throughput_jobs_per_min ?? 0) > 0 && (
                <div className="text-xs text-gray-500 pt-1">
                  Throughput: {snapshot?.throughput_jobs_per_min ?? cmd?.snapshot?.throughput_jobs_per_min} jobs/min ·{" "}
                  avg {snapshot?.throughput_avg_s ?? cmd?.snapshot?.throughput_avg_s}s
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No GPU workers registered.</p>
          )}
        </Section>

        {/* Queue */}
        <Section
          title="Queue"
          badge={queueDepth > 0 ? `${queueDepth} queued` : "empty"}
          icon={Layers}
          defaultOpen={isStuck || isBackedUp || queueDepth > 0}
        >
          {queueDepth > 0 || activeJobs > 0 ? (
            <div className="space-y-3">
              {isStuck && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span>Queue stuck — {queueDepth} jobs waiting but nothing is processing</span>
                </div>
              )}
              {isBackedUp && !isStuck && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span>Queue backed up — {queueDepth} jobs waiting</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                  <div className="text-lg font-mono text-white">{queueDefault}</div>
                  <div className="text-[10px] text-gray-500">Default</div>
                </div>
                <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                  <div className="text-lg font-mono text-white">{queuePri}</div>
                  <div className="text-[10px] text-gray-500">Priority</div>
                </div>
                <div className="rounded-lg bg-card border border-border px-3 py-2 text-center">
                  <div className="text-lg font-mono text-white">{queuePub}</div>
                  <div className="text-[10px] text-gray-500">Publisher</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {activeJobs > 0 && (
                  <span className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    {activeJobs} actively processing
                  </span>
                )}
                {throughputRate > 0 && (
                  <span>{throughputRate} jobs/min</span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Queue is empty — no pending jobs.</p>
          )}
        </Section>

        {/* Diagnostics */}
        <Section
          title="Diagnostics"
          badge={diagnosis ? (diagnosis.severity || "ok") : "—"}
          icon={Search}
          defaultOpen={!!diagnosis?.actions?.length}
        >
          <div className="space-y-3">
            {!diagnosis && (
              <button
                onClick={handleDiagnose}
                disabled={diagnosing}
                className="w-full py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-sm transition-colors border border-border disabled:opacity-50 disabled:cursor-wait"
              >
                {diagnosing ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw size={14} className="animate-spin" />
                    Analyzing system...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Search size={14} />
                    Run Diagnosis
                  </span>
                )}
              </button>
            )}
            {diagnosis && (
              <>
                <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {diagnosis.analysis}
                </div>
                {diagnosis.actions && diagnosis.actions.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500 font-medium">Suggested Actions</div>
                    {diagnosis.actions.map((action: any, i: number) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                        <Wrench size={14} className="text-accent shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-200">{action.label}</div>
                          {action.detail && <div className="text-xs text-gray-500 mt-0.5">{action.detail}</div>}
                        </div>
                        {action.auto_fixable && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/20 text-accent shrink-0">auto-fix</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => { setDiagnosis(null); handleDiagnose() }}
                  disabled={diagnosing}
                  className="text-xs text-accent hover:underline disabled:opacity-50"
                >
                  {diagnosing ? "Analyzing..." : "Re-run diagnosis"}
                </button>
              </>
            )}
          </div>
        </Section>

        {/* Launch Readiness Checks */}
        <Section
          title="Launch Readiness"
          badge="checks"
          icon={ClipboardCheck}
        >
          <ChecksPanel />
        </Section>

        {/* Autopilot */}
        <Section
          title="Autopilot"
          badge={`${autopilotRecent.length} recent`}
          icon={Shield}
        >
          {autopilotRecent.length > 0 ? (
            <div className="divide-y divide-border">
              {autopilotRecent.map((e, i) => <AutopilotEntry key={i} entry={e} />)}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No recent autopilot actions.</p>
          )}
        </Section>

        {/* Health Targets / SLOs */}
        <Section
          title="Health Targets"
          badge={slosTotal > 0 ? `${slosMet}/${slosTotal} met` : "—"}
          icon={Target}
        >
          {Array.isArray(slos) && slos.length > 0 ? (
            <div className="divide-y divide-border">
              {slos.map((s: any, i: number) => <SloRow key={i} slo={s} />)}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No SLO data available.</p>
          )}
        </Section>

        {/* History — show recent autopilot + job stats */}
        <Section
          title="History"
          badge={cmd?.jobs_total ? `${cmd.jobs_total.toLocaleString()} jobs` : ""}
          icon={Clock}
        >
          {autopilotRecent.length > 0 ? (
            <div className="space-y-3">
              {cmd?.jobs_total ? (
                <p className="text-xs text-gray-500">
                  {cmd.jobs_total.toLocaleString()} total jobs processed.
                </p>
              ) : null}
              <div className="text-xs text-gray-500 font-medium">Recent actions</div>
              <div className="divide-y divide-border">
                {autopilotRecent.map((e, i) => <AutopilotEntry key={i} entry={e} />)}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              {cmd?.jobs_total
                ? `${cmd.jobs_total.toLocaleString()} total jobs processed. No recent autopilot actions.`
                : "No activity yet. Waiting for GPU workers to connect."}
            </p>
          )}
        </Section>
      </div>

      {/* Bottom padding for chat FAB */}
      <div className="h-20" />
    </div>
  )
}
